import { useParams } from "react-router-dom";
import {
  getClearReportType,
  getReportTypesFilteringOptions,
  getUserName,
} from "../../common/EntitiesManagement";
import useGlobalContext from "../../hooks/useGlobalContext";
import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import localize from "../../common/localize";
import ErrorMessage from "../../components/common/ErrorMessage";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import ModalBackground from "../../components/common/ModalBackground";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import { mutate } from "swr";
import EntityPrinter from "../../components/common/EntityPrinter";
import ReportSetup from "../event/components/report/Reportsetup";
import { Lock, LockOpen } from "@mui/icons-material";
import { REPORT_TYPE_ENDPOINT } from "../../services/config";
import AccessStatus from "../dailyRoute/components/AccessStatus";

export default function ReportTypes() {
  const {
    config,
    reportTypeModel,
    setReportTypeModel,
    reportTypeData,
    pageIndexReportType,
    setPageIndexReportType,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [reportTypeDataFiltered, setReportTypeDataFiltered] = useState(
    reportTypeData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const userName = getUserName();

  useEffect(() => {
    setBasicFilterOptions(getReportTypesFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const reportTypeDataObj = reportTypeData.Data.find(
        (reportTypeDataObj) => reportTypeDataObj.Id === Number(id)
      );
      setReportTypeModel(reportTypeDataObj);
    }
  }, [id]);

  useEffect(() => {
    setReportTypeDataFiltered(reportTypeData.Data);
  }, [reportTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setReportTypeModel(getClearReportType());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([REPORT_TYPE_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexReportType !== 0 && reportTypeData.Data.length === 1) {
        setPageIndexReportType(pageIndexReportType - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${REPORT_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const obtainsReportSetupData = (data) => ({
    Name: data.Name,
    Description: data.Description,
    CreatedBy: data.CreatedBy,
    IsPublic: data.IsPublic,
    ReportSections: JSON.stringify(data.ReportSections),
  });

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${REPORT_TYPE_ENDPOINT}/${data.Id}`}`,
      obtainsReportSetupData(data),
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${REPORT_TYPE_ENDPOINT}`}`,
      obtainsReportSetupData(data),
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleIsPublicChange = (e) => {
    setReportTypeModel({
      ...reportTypeModel,
      [e.target.name]: e.target.checked,
    });
  };

  const getComponent = (showModal, setShowModal, title, model, setModel) => {
    return (
      <AddOrEditBasicEntity
        model={model}
        setModel={setModel}
        nameMandatory
        isReadonly={!model?.IsPublic && model.CreatedBy !== userName}
        customComponent={
          <Box className="flex flex-col gap-2">
            <FormControlLabel
              disabled={!model?.IsPublic && model.CreatedBy !== userName}
              size="small"
              name="IsPublic"
              control={
                <Checkbox
                  size="small"
                  name="IsPublic"
                  checked={model?.IsPublic}
                  onChange={handleIsPublicChange}
                  icon={<Lock />}
                  checkedIcon={<LockOpen />}
                />
              }
              label={
                <span style={{ fontSize: "0.8em" }}>
                  <PageTitleSubtitle
                    useEventTitleStyle
                    useFieldTitleStyle
                    title={
                      model?.IsPublic ? localize.is_public : localize.is_private
                    }
                    subtitle={
                      model?.IsPublic
                        ? localize.is_public_complement
                        : localize.is_private_complement
                    }
                  />
                </span>
              }
            />

            <ReportSetup
              showGenerateInformModal={showModal}
              setShowGenerateInformModal={setShowModal}
              editionMode
              title={title}
              reportSetupModel={model}
              setReportSetupModel={setModel}
              isReadOnly={!model?.IsPublic && model.CreatedBy !== userName}
            />
          </Box>
        }
        customWidth="440px"
      />
    );
  };

  const disableSaveChanges = () =>
    !reportTypeModel?.Name ||
    reportTypeModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.edition_level,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.report_types}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={`${REPORT_TYPE_ENDPOINT}/${userName}`}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="report_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_request_type}
          onClickToSubmit={() => {
            handleUpdateRequest(reportTypeModel);
            setShowEditModal(false);
            setReportTypeModel(getClearReportType());
          }}
          hideSubmitButton={
            !reportTypeModel.IsPublic && reportTypeModel.CreatedBy !== userName
          }
          cancelButtonText={
            !reportTypeModel.IsPublic && reportTypeModel.CreatedBy !== userName
              ? localize.close
              : localize.cancel
          }
          onClickToCancel={() => {
            setShowEditModal(false);
            setReportTypeModel(getClearReportType());
          }}
          Component={getComponent(
            showEditModal,
            setShowEditModal,
            localize.edit_report_type,
            reportTypeModel,
            setReportTypeModel
          )}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.report_types}
            subtitle={localize.report_types_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="report_type"
            handleAddBtn={() => {
              setReportTypeModel(getClearReportType());
              setShowAddModal(true);
            }}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_report_type}
                onClickToSubmit={() => {
                  handleCreateRequest(reportTypeModel);
                  setShowAddModal(false);
                  setReportTypeModel(getClearReportType());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setReportTypeModel(getClearReportType());
                }}
                Component={getComponent(
                  showAddModal,
                  setShowAddModal,
                  localize.new_report_type,
                  reportTypeModel,
                  setReportTypeModel
                )}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={reportTypeModel.Name}
          entityType={localize.report_type}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setReportTypeModel(getClearReportType());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(reportTypeModel.Id);
            setShowDeleteModal(false);
            setReportTypeModel(getClearReportType());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexReportType}
            setPageIndex={setPageIndexReportType}
            dataArray={reportTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          reportTypeData.Data?.length !== 0
            ? reportTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      detailsButtonText={
                        !item?.IsPublic && item.CreatedBy !== userName
                          ? localize.details
                          : undefined
                      }
                      onClickForDetailButton={() => {
                        setReportTypeModel({
                          ...item,
                          ReportSections: JSON.parse(item.ReportSections),
                        });
                        setShowEditModal(true);
                      }}
                      hideDeleteButton={
                        !item?.IsPublic && item.CreatedBy !== userName
                      }
                      hideEditButton={
                        !item?.IsPublic && item.CreatedBy !== userName
                      }
                      onClickForEditButton={() => {
                        setReportTypeModel({
                          ...item,
                          ReportSections: JSON.parse(item.ReportSections),
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setReportTypeModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  <AccessStatus isPublic={item.IsPublic} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(6)]
        }
      />
    </div>
  );
}
